/**
 * SnapDialog.ts
 * 
 * A class to handle draggable dialogs with Windows 11-style edge snapping behavior.
 * When dragged to screen edges, the dialog will snap and resize to fill a portion of the screen.
 */

/**
 * Interface for position tracking with x, y coordinates
 */
interface Position {
    x: number
    y: number
}

/**
 * Interface for position with left, top coordinates
 */
interface PositionCoordinates {
    left: number
    top: number
}

/**
 * Interface for tracking mouse movement with timestamp
 */
interface MousePosition extends Position {
    time: number
}

/**
 * Interface for tracking edge movement directions
 */
interface EdgeMovement {
    left: boolean
    right: boolean
    top: boolean
    bottom: boolean
}

/**
 * Interface for snap state tracking
 */
interface SnapState {
    left: boolean
    right: boolean
    top: boolean
    topLeft: boolean
    topRight: boolean
    bottom: boolean
    bottomLeft: boolean
    bottomRight: boolean
    maximized: boolean
}

/**
 * Interface for snap intention timers
 */
interface SnapIntentionTimers {
    [key: string]: number
}

/**
 * Configuration options for SnapDialog
 */
interface SnapDialogConfig {
    snapRatio?: number
    snapThreshold?: number
    snapAnimationDuration?: number
    iframeBuffer?: number
    closeButtonSelector?: string
    collapseButtonSelector?: string
    onClose?: () => void
    onCollapse?: (isCollapsed: boolean) => void
    title?: string
    iframeUrl?: string
}

export class SnapDialog {
    // Configuration constants
    private readonly SNAP_RATIO: number
    private readonly SNAP_THRESHOLD: number
    private readonly SNAP_ANIMATION_DURATION: number
    private readonly IFRAME_BUFFER: number
    private readonly CLOSE_BUTTON_SELECTOR: string
    private readonly COLLAPSE_BUTTON_SELECTOR: string
    
    private dialog: HTMLElement
    private header: HTMLElement
    private closeButton: HTMLElement | null = null
    private collapseButton: HTMLElement | null = null
    private contentContainer: HTMLElement | null = null
    private isCollapsed = false
    private onClose: (() => void) | null = null
    private onCollapse: ((isCollapsed: boolean) => void) | null = null
    private isDragging = false
    private isSnapped = false
    private originalWidth: number
    private originalHeight: number
    private originalPosition: PositionCoordinates
    private collapsedHeight = 40 // Height of dialog when collapsed
    private dragOffset: Position = { x: 0, y: 0 }
    
    // Movement vector tracking properties
    private previousMousePositions: Array<MousePosition> = []
    private movementVectorX = 0
    private movementVectorY = 0
    private intentionThreshold = 0.7 // Value between 0-1 for direction consistency
    private velocityThreshold = 0.3 // Minimum velocity to consider as intentional
    private positionHistorySize = 5 // Number of positions to track for vector calculation
    private snapIntentionTimers: SnapIntentionTimers = {}
    private snapIntentionDelay = 150 // ms to maintain direction before snapping
    private movingTowardEdge: EdgeMovement = {
        left: false,
        right: false,
        top: false,
        bottom: false
    }
    
    // Store snap state to know where the dialog is snapped
    private snapState: SnapState = {
        left: false,
        right: false,
        top: false,
        topLeft: false,
        topRight: false,
        bottom: false,
        bottomLeft: false,
        bottomRight: false,
        maximized: false
    }

    private documentMouseMoveListener: (e: MouseEvent) => void
    private documentMouseUpListener: (e: MouseEvent) => void
    private windowResizeListener: () => void
    private iframeMouseTrapListener: (e: MouseEvent) => void
    private closeButtonClickListener: (e: MouseEvent) => void
    private collapseButtonClickListener: (e: MouseEvent) => void
    private handleMouseDownBound: (e: MouseEvent) => void

    /**
     * Create a new SnapDialog instance
     * @param dialogElement The dialog element to make snappable
     * @param headerElement The header element used for dragging
     * @param config Optional configuration options
     */
    constructor(dialogElement: HTMLElement, headerElement: HTMLElement, config?: SnapDialogConfig) {
        // Set defaults for configurable properties
        this.SNAP_RATIO = config?.snapRatio ?? 0.4
        this.SNAP_THRESHOLD = config?.snapThreshold ?? 20
        this.SNAP_ANIMATION_DURATION = config?.snapAnimationDuration ?? 200
        this.IFRAME_BUFFER = config?.iframeBuffer ?? 10
        this.CLOSE_BUTTON_SELECTOR = config?.closeButtonSelector ?? '.iframe-dialog-close'
        this.COLLAPSE_BUTTON_SELECTOR = config?.collapseButtonSelector ?? '.iframe-dialog-collapse'
        this.onClose = config?.onClose ?? null
        this.onCollapse = config?.onCollapse ?? null
        
        this.dialog = dialogElement
        this.header = headerElement
        this.originalWidth = this.dialog.offsetWidth
        this.originalHeight = this.dialog.offsetHeight
        this.originalPosition = {
            left: parseInt(this.dialog.style.left || '0', 10),
            top: parseInt(this.dialog.style.top || '0', 10)
        }

        // Ensure all handlers are properly bound to this instance
        this.documentMouseMoveListener = this.handleMouseMove.bind(this)
        this.documentMouseUpListener = this.handleMouseUp.bind(this)
        this.windowResizeListener = this.handleWindowResize.bind(this)
        this.iframeMouseTrapListener = this.handleIframeMouseTrap.bind(this)
        this.closeButtonClickListener = this.handleCloseClick.bind(this)
        this.collapseButtonClickListener = this.handleCollapseClick.bind(this)
        this.handleMouseDownBound = this.handleMouseDown.bind(this)

        this.initialize()
    }

    /**
     * Initialize event listeners
     */
    private initialize(): void {
        // Make header draggable
        this.header.addEventListener('mousedown', this.handleMouseDownBound)
        
        // Listen for window resize to adjust snapped positions
        window.addEventListener('resize', this.windowResizeListener)

        // Find and set up close button if it exists
        this.setupCloseButton()

        // Find and set up collapse button if it exists
        this.setupCollapseButton()

        // Create invisible overlay for iframe mouse events when dragging
        this.setupIframeMouseTrap()
    }

    /**
     * Find and set up the close button event handler
     */
    private setupCloseButton(): void {
        // Try to find the close button within the dialog
        const closeButton = this.dialog.querySelector(this.CLOSE_BUTTON_SELECTOR)
        
        console.log("CLOSE BUTTON", closeButton, this.CLOSE_BUTTON_SELECTOR)
        if (closeButton instanceof HTMLElement) {
            // Store reference to the close button
            this.closeButton = closeButton
            
            // Add direct click handler to close button - important to use the bound listener
            this.closeButton.addEventListener('click', this.closeButtonClickListener)
            
            // Stop mousedown on close button to prevent drag behavior
            this.closeButton.addEventListener('mousedown', (e: MouseEvent) => {
                e.stopPropagation()
            })
            
            // Make the close button more visible in dev mode
            if (process.env.NODE_ENV === 'development') {
                console.log('SnapDialog: Close button found and handler attached', this.closeButton)
            }
        } else {
            // Log warning if close button not found
            if (process.env.NODE_ENV === 'development') {
                console.warn(`SnapDialog: Close button not found with selector "${this.CLOSE_BUTTON_SELECTOR}"`)
            }
        }
    }

    /**
     * Find and set up the collapse button event handler
     */
    private setupCollapseButton(): void {
        // Try to find the collapse button within the dialog
        const collapseButton = this.dialog.querySelector(this.COLLAPSE_BUTTON_SELECTOR)
        
        console.log("COLLAPSE BUTTON", collapseButton, this.COLLAPSE_BUTTON_SELECTOR)
        if (collapseButton instanceof HTMLElement) {
            // Store reference to the collapse button
            this.collapseButton = collapseButton
            
            // Add direct click handler to collapse button - important to use the bound listener
            this.collapseButton.addEventListener('click', this.collapseButtonClickListener)
            
            // Stop mousedown on collapse button to prevent drag behavior
            this.collapseButton.addEventListener('mousedown', (e: MouseEvent) => {
                e.stopPropagation()
            })
            
            // Make the collapse button more visible in dev mode
            if (process.env.NODE_ENV === 'development') {
                console.log('SnapDialog: Collapse button found and handler attached', this.collapseButton)
            }
        } else {
            // Log warning if collapse button not found
            if (process.env.NODE_ENV === 'development') {
                console.warn(`SnapDialog: Collapse button not found with selector "${this.COLLAPSE_BUTTON_SELECTOR}"`)
            }
        }
    }

    /**
     * Handle close button click
     */
    private handleCloseClick(e: MouseEvent): void {
        // Prevent default behavior and stop propagation
        e.preventDefault()
        e.stopPropagation()
        
        // Log that the close handler was triggered (in dev mode)
        if (process.env.NODE_ENV === 'development') {
            console.log('SnapDialog: Close button clicked')
        }
        
        // Hide the dialog
        this.dialog.style.display = 'none'
        
        // Call the onClose callback if provided
        if (this.onClose) {
            this.onClose()
        }
    }

    /**
     * Handle collapse button click
     */
    private handleCollapseClick(e: MouseEvent): void {
        // Prevent default behavior and stop propagation
        e.preventDefault()
        e.stopPropagation()
        
        // Log that the collapse handler was triggered (in dev mode)
        if (process.env.NODE_ENV === 'development') {
            console.log('SnapDialog: Collapse button clicked')
        }
        
        // Toggle collapse state
        this.isCollapsed = !this.isCollapsed
        
        // Find the content container if not already stored
        if (!this.contentContainer) {
            this.contentContainer = this.dialog.querySelector('.iframe-dialog-content')
        }

        // Find the collapse button icon
        const collapseIcon = this.collapseButton?.querySelector('i')
        
        if (this.isCollapsed) {
            // Collapse the dialog - save current height
            this.originalHeight = this.dialog.offsetHeight
            
            // Remove height styling completely instead of setting a fixed height
            this.dialog.style.height = ''
            
            // Remove resize ability when collapsed
            this.dialog.style.resize = 'none'
            
            // Add collapsed class for CSS targeting
            this.dialog.classList.add('collapsed')
            
            if (this.contentContainer) {
                this.contentContainer.style.display = 'none'
            }
            
            // Change icon to plus
            if (collapseIcon) {
                collapseIcon.className = 'fas fa-plus'
            }
        } else {
            // Expand the dialog
            this.dialog.style.height = `${this.originalHeight}px`
            
            // Restore resize ability
            this.dialog.style.resize = 'both'
            
            // Remove collapsed class
            this.dialog.classList.remove('collapsed')
            
            if (this.contentContainer) {
                this.contentContainer.style.display = 'block'
            }
            
            // Change icon back to minus
            if (collapseIcon) {
                collapseIcon.className = 'fas fa-minus'
            }
        }
        
        // Call the onCollapse callback if provided
        if (this.onCollapse) {
            this.onCollapse(this.isCollapsed)
        }
    }

    /**
     * Set up an invisible overlay to catch mouse events over iframes
     */
    private setupIframeMouseTrap(): void {
        // Add global CSS for the mouse trap element
        const style = document.createElement('style')
        style.innerHTML = `
          .iframe-mouse-trap {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 9999999;
            display: none;
            cursor: grabbing !important;
          }
        `
        document.head.appendChild(style)
    }

    /**
     * Create and show the iframe mouse trap
     */
    private showIframeMouseTrap(): void {
        const existingTrap = document.querySelector('.iframe-mouse-trap')
        if (existingTrap) {
            (existingTrap as HTMLElement).style.display = 'block'
            return
        }

        const trap = document.createElement('div')
        trap.className = 'iframe-mouse-trap'
        trap.style.display = 'block'
        document.body.appendChild(trap)

        // Add event listeners to the trap
        trap.addEventListener('mousemove', this.iframeMouseTrapListener)
        trap.addEventListener('mouseup', this.documentMouseUpListener)
    }

    /**
     * Hide the iframe mouse trap
     */
    private hideIframeMouseTrap(): void {
        const trap = document.querySelector('.iframe-mouse-trap')
        if (trap) {
            (trap as HTMLElement).style.display = 'none'
            trap.removeEventListener('mousemove', this.iframeMouseTrapListener)
            trap.removeEventListener('mouseup', this.documentMouseUpListener)
        }
    }

    /**
     * Handle mouse events from the iframe trap
     */
    private handleIframeMouseTrap(e: MouseEvent): void {
        this.handleMouseMove(e)
    }

    /**
     * Remove all event listeners and cleanup
     */
    public destroy(): void {
        // Remove our properly bound event listeners
        this.header.removeEventListener('mousedown', this.handleMouseDownBound)
        document.removeEventListener('mousemove', this.documentMouseMoveListener)
        document.removeEventListener('mouseup', this.documentMouseUpListener)
        window.removeEventListener('resize', this.windowResizeListener)
        
        // Remove close button listener if it exists
        if (this.closeButton) {
            this.closeButton.removeEventListener('click', this.closeButtonClickListener)
        }
        
        // Remove collapse button listener if it exists
        if (this.collapseButton) {
            this.collapseButton.removeEventListener('click', this.collapseButtonClickListener)
        }
        
        this.hideIframeMouseTrap()
        
        // Remove the trap element
        const trap = document.querySelector('.iframe-mouse-trap')
        if (trap) {
            trap.remove()
        }
        
        // Clear any pending timers
        Object.keys(this.snapIntentionTimers).forEach(key => {
            if (this.snapIntentionTimers[key]) {
                clearTimeout(this.snapIntentionTimers[key])
            }
        })
    }

    /**
     * Show the dialog if it was hidden
     */
    public show(): void {
        this.dialog.style.display = 'block'
    }

    /**
     * Hide the dialog
     */
    public hide(): void {
        this.dialog.style.display = 'none'
    }

    /**
     * Track mouse movement and calculate vectors
     */
    private trackMouseMovement(x: number, y: number): void {
        const now = Date.now()
        
        // Add current position to history
        this.previousMousePositions.push({
            x,
            y,
            time: now
        })
        
        // Limit history size
        if (this.previousMousePositions.length > this.positionHistorySize) {
            this.previousMousePositions.shift()
        }
        
        // Need at least 2 positions to calculate direction
        if (this.previousMousePositions.length >= 2) {
            const oldest = this.previousMousePositions[0]
            const newest = this.previousMousePositions[this.previousMousePositions.length - 1]
            
            // Calculate movement vector
            const timeElapsed = (newest.time - oldest.time) / 1000 // convert to seconds
            if (timeElapsed > 0) {
                this.movementVectorX = (newest.x - oldest.x) / timeElapsed
                this.movementVectorY = (newest.y - oldest.y) / timeElapsed
            }
            
            // Determine movement direction toward edges
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight
            
            // Normalize vectors for direction calculation
            const vectorMagnitude = Math.sqrt(this.movementVectorX * this.movementVectorX + this.movementVectorY * this.movementVectorY)
            const normalizedVectorX = vectorMagnitude > 0 ? this.movementVectorX / vectorMagnitude : 0
            const normalizedVectorY = vectorMagnitude > 0 ? this.movementVectorY / vectorMagnitude : 0
            
            // Check if moving toward edges - use normalized vectors for direction
            this.movingTowardEdge.left = normalizedVectorX < -this.intentionThreshold
            this.movingTowardEdge.right = normalizedVectorX > this.intentionThreshold
            this.movingTowardEdge.top = normalizedVectorY < -this.intentionThreshold
            this.movingTowardEdge.bottom = normalizedVectorY > this.intentionThreshold
        }
    }

    /**
     * Check if the movement has enough velocity to be considered intentional
     */
    private hasIntentionalVelocity(): boolean {
        const vectorMagnitude = Math.sqrt(this.movementVectorX * this.movementVectorX + this.movementVectorY * this.movementVectorY)
        return vectorMagnitude > this.velocityThreshold
    }

    /**
     * Start a timer to track consistent edge approach
     */
    private startEdgeIntentionTimer(edge: string, callback: () => void): void {
        // Clear any existing timer for this edge
        if (this.snapIntentionTimers[edge]) {
            clearTimeout(this.snapIntentionTimers[edge])
        }
        
        // Set new timer
        this.snapIntentionTimers[edge] = window.setTimeout(() => {
            callback()
            this.snapIntentionTimers[edge] = 0
        }, this.snapIntentionDelay)
    }

    /**
     * Clear all snap intention timers
     */
    private clearSnapIntentionTimers(): void {
        Object.keys(this.snapIntentionTimers).forEach(key => {
            if (this.snapIntentionTimers[key]) {
                clearTimeout(this.snapIntentionTimers[key])
                this.snapIntentionTimers[key] = 0
            }
        })
    }

    /**
     * Handle mouse down on the dialog header
     */
    private handleMouseDown(e: MouseEvent): void {
        // Skip drag initialization if the click came from the close button
        if (this.closeButton && (e.target === this.closeButton || this.closeButton.contains(e.target as Node))) {
            return
        }
        
        // Skip drag initialization if the click came from the collapse button
        if (this.collapseButton && (e.target === this.collapseButton || this.collapseButton.contains(e.target as Node))) {
            return
        }
        
        // Prevent default behavior
        e.preventDefault()
        
        this.isDragging = true
        
        const dialogRect = this.dialog.getBoundingClientRect()
        this.dragOffset.x = e.clientX - dialogRect.left
        this.dragOffset.y = e.clientY - dialogRect.top
        
        this.dialog.style.transition = 'none'
        this.dialog.style.cursor = 'grabbing'
        this.header.style.cursor = 'grabbing'

        // Reset movement tracking
        this.previousMousePositions = []
        this.movementVectorX = 0
        this.movementVectorY = 0
        this.clearSnapIntentionTimers()

        // Add highlight class to show we're actively dragging
        this.dialog.classList.add('active-drag')

        // Only add event listeners once
        document.addEventListener('mousemove', this.documentMouseMoveListener)
        document.addEventListener('mouseup', this.documentMouseUpListener)
        
        // Show the iframe mouse trap to capture events
        this.showIframeMouseTrap()
    }

    /**
     * Handle mouse movement while dragging
     */
    private handleMouseMove(e: MouseEvent): void {
        if (!this.isDragging) return

        // Prevent default to avoid text selection, etc.
        e.preventDefault()

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        
        // Calculate new position
        let left = e.clientX - this.dragOffset.x
        let top = e.clientY - this.dragOffset.y
        
        // Track movement vector
        this.trackMouseMovement(e.clientX, e.clientY)
        
        // Bounds checking to keep dialog on screen
        const dialogWidth = this.dialog.offsetWidth
        const dialogHeight = this.dialog.offsetHeight
        
        // If dialog was previously snapped and is now being dragged again,
        // restore original dimensions and exit snap mode
        if (this.isSnapped) {
            // Only unsnap after moving a bit to prevent accidental unsnapping
            const moveThreshold = 5
            const distanceMoved = Math.sqrt(
                Math.pow(e.clientX - (parseInt(this.dialog.style.left, 10) + this.dragOffset.x), 2) +
                Math.pow(e.clientY - (parseInt(this.dialog.style.top, 10) + this.dragOffset.y), 2)
            )
            
            if (distanceMoved > moveThreshold) {
                this.restoreFromSnap()
            }
        }

        // Check for snapping to edges if not already snapped
        if (!this.isSnapped) {
            // Top edge double-click check for maximize
            if (top < 5 && e.detail === 2) {
                this.snapToMaximize()
                return
            }
            
            const hasIntention = this.hasIntentionalVelocity()
            
            // Left edge snap
            if (left < this.SNAP_THRESHOLD && this.movingTowardEdge.left && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                // Start intention timer for consistent approach
                if (!this.snapIntentionTimers.left) {
                    this.startEdgeIntentionTimer('left', () => this.snapToLeft())
                }
                return
            } else if (this.snapIntentionTimers.left && !this.movingTowardEdge.left) {
                clearTimeout(this.snapIntentionTimers.left)
                this.snapIntentionTimers.left = 0
            }
            
            // Right edge snap
            if (left + dialogWidth > viewportWidth - this.SNAP_THRESHOLD && this.movingTowardEdge.right && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.right) {
                    this.startEdgeIntentionTimer('right', () => this.snapToRight())
                }
                return
            } else if (this.snapIntentionTimers.right && !this.movingTowardEdge.right) {
                clearTimeout(this.snapIntentionTimers.right)
                this.snapIntentionTimers.right = 0
            }
            
            // Top edge snap
            if (top < this.SNAP_THRESHOLD && this.movingTowardEdge.top && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.top) {
                    this.startEdgeIntentionTimer('top', () => this.snapToTop())
                }
                return
            } else if (this.snapIntentionTimers.top && !this.movingTowardEdge.top) {
                clearTimeout(this.snapIntentionTimers.top)
                this.snapIntentionTimers.top = 0
            }
            
            // Bottom edge snap
            if (top + dialogHeight > viewportHeight - this.SNAP_THRESHOLD && this.movingTowardEdge.bottom && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.bottom) {
                    this.startEdgeIntentionTimer('bottom', () => this.snapToBottom())
                }
                return
            } else if (this.snapIntentionTimers.bottom && !this.movingTowardEdge.bottom) {
                clearTimeout(this.snapIntentionTimers.bottom)
                this.snapIntentionTimers.bottom = 0
            }
            
            // Corner snaps - require moving toward both edges
            // Top-left corner snap
            if (left < this.SNAP_THRESHOLD && top < this.SNAP_THRESHOLD && 
                this.movingTowardEdge.left && this.movingTowardEdge.top && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.topLeft) {
                    this.startEdgeIntentionTimer('topLeft', () => this.snapToTopLeft())
                }
                return
            } else if (this.snapIntentionTimers.topLeft && 
                    !(this.movingTowardEdge.left && this.movingTowardEdge.top)) {
                clearTimeout(this.snapIntentionTimers.topLeft)
                this.snapIntentionTimers.topLeft = 0
            }
            
            // Top-right corner snap
            if (left + dialogWidth > viewportWidth - this.SNAP_THRESHOLD && top < this.SNAP_THRESHOLD &&
                this.movingTowardEdge.right && this.movingTowardEdge.top && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.topRight) {
                    this.startEdgeIntentionTimer('topRight', () => this.snapToTopRight())
                }
                return
            } else if (this.snapIntentionTimers.topRight && 
                    !(this.movingTowardEdge.right && this.movingTowardEdge.top)) {
                clearTimeout(this.snapIntentionTimers.topRight)
                this.snapIntentionTimers.topRight = 0
            }
            
            // Bottom-left corner snap
            if (left < this.SNAP_THRESHOLD && top + dialogHeight > viewportHeight - this.SNAP_THRESHOLD &&
                this.movingTowardEdge.left && this.movingTowardEdge.bottom && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.bottomLeft) {
                    this.startEdgeIntentionTimer('bottomLeft', () => this.snapToBottomLeft())
                }
                return
            } else if (this.snapIntentionTimers.bottomLeft && 
                    !(this.movingTowardEdge.left && this.movingTowardEdge.bottom)) {
                clearTimeout(this.snapIntentionTimers.bottomLeft)
                this.snapIntentionTimers.bottomLeft = 0
            }
            
            // Bottom-right corner snap
            if (left + dialogWidth > viewportWidth - this.SNAP_THRESHOLD && 
                top + dialogHeight > viewportHeight - this.SNAP_THRESHOLD &&
                this.movingTowardEdge.right && this.movingTowardEdge.bottom && hasIntention) {
                this.dialog.classList.add('snap-highlight')
                
                if (!this.snapIntentionTimers.bottomRight) {
                    this.startEdgeIntentionTimer('bottomRight', () => this.snapToBottomRight())
                }
                return
            } else if (this.snapIntentionTimers.bottomRight && 
                    !(this.movingTowardEdge.right && this.movingTowardEdge.bottom)) {
                clearTimeout(this.snapIntentionTimers.bottomRight)
                this.snapIntentionTimers.bottomRight = 0
            }
            
            // If not near any edges or not moving toward edges, remove highlight
            if (!this.hasAnySnapIntentionTimers()) {
                this.dialog.classList.remove('snap-highlight')
            }
        }
        
        // Normal movement (no snapping)
        // Keep dialog within viewport
        left = Math.max(0, Math.min(left, viewportWidth - dialogWidth))
        top = Math.max(0, Math.min(top, viewportHeight - dialogHeight))
        
        this.dialog.style.left = `${left}px`
        this.dialog.style.top = `${top}px`
    }

    /**
     * Check if any snap intention timers are active
     */
    private hasAnySnapIntentionTimers(): boolean {
        return Object.values(this.snapIntentionTimers).some(timer => timer !== 0)
    }

    /**
     * Handle mouse up to end dragging
     */
    private handleMouseUp(e: MouseEvent): void {
        this.isDragging = false
        this.dialog.style.cursor = 'default'
        this.header.style.cursor = 'grab'
        
        // Hide the iframe mouse trap
        this.hideIframeMouseTrap()
        
        // Remove highlight classes
        this.dialog.classList.remove('active-drag')
        this.dialog.classList.remove('snap-highlight')
        
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        // Store current position as original position if not snapped
        if (!this.isSnapped) {
            this.originalPosition = {
                left: parseInt(this.dialog.style.left || '0', 10),
                top: parseInt(this.dialog.style.top || '0', 10)
            }
            this.originalWidth = this.dialog.offsetWidth
            this.originalHeight = this.dialog.offsetHeight
        }
        
        // Remove document event listeners
        document.removeEventListener('mousemove', this.documentMouseMoveListener)
        document.removeEventListener('mouseup', this.documentMouseUpListener)
    }

    /**
     * Handle window resize to adjust snapped dialogs
     */
    private handleWindowResize(): void {
        if (!this.isSnapped) return
        
        // Readjust the dialog based on its snap state
        if (this.snapState.left) this.snapToLeft()
        else if (this.snapState.right) this.snapToRight()
        else if (this.snapState.top) this.snapToTop()
        else if (this.snapState.bottom) this.snapToBottom()
        else if (this.snapState.topLeft) this.snapToTopLeft()
        else if (this.snapState.topRight) this.snapToTopRight()
        else if (this.snapState.bottomLeft) this.snapToBottomLeft()
        else if (this.snapState.bottomRight) this.snapToBottomRight()
        else if (this.snapState.maximized) this.snapToMaximize()
    }

    /**
     * Reset snap state
     */
    private resetSnapState(): void {
        Object.keys(this.snapState).forEach(key => {
            this.snapState[key] = false
        })
    }

    /**
     * Restore dialog to its pre-snapped state
     */
    private restoreFromSnap(): void {
        if (!this.isSnapped) return
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.width = `${this.originalWidth}px`
        this.dialog.style.height = `${this.originalHeight}px`
        this.dialog.style.left = `${this.originalPosition.left}px`
        this.dialog.style.top = `${this.originalPosition.top}px`
        this.dialog.style.maxWidth = ''
        this.dialog.style.maxHeight = ''
        
        this.isSnapped = false
        this.resetSnapState()
        
        // Clear transition after animation completes
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to left edge
     */
    private snapToLeft(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportHeight = window.innerHeight
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = '0px'
        this.dialog.style.top = '0px'
        this.dialog.style.width = `${window.innerWidth * this.SNAP_RATIO}px`
        this.dialog.style.height = `${viewportHeight}px`
        this.dialog.style.maxWidth = `${window.innerWidth * this.SNAP_RATIO}px`
        this.dialog.style.maxHeight = `${viewportHeight}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.left = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to right edge
     */
    private snapToRight(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const rightOffset = viewportWidth * (1 - this.SNAP_RATIO)
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = `${rightOffset}px`
        this.dialog.style.top = '0px'
        this.dialog.style.width = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.height = `${viewportHeight}px`
        this.dialog.style.maxWidth = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.maxHeight = `${viewportHeight}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.right = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to top edge
     */
    private snapToTop(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = '0px'
        this.dialog.style.top = '0px'
        this.dialog.style.width = `${viewportWidth}px`
        this.dialog.style.height = `${window.innerHeight * this.SNAP_RATIO}px`
        this.dialog.style.maxWidth = `${viewportWidth}px`
        this.dialog.style.maxHeight = `${window.innerHeight * this.SNAP_RATIO}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.top = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to bottom edge
     */
    private snapToBottom(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const bottomOffset = viewportHeight * (1 - this.SNAP_RATIO)
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = '0px'
        this.dialog.style.top = `${bottomOffset}px`
        this.dialog.style.width = `${viewportWidth}px`
        this.dialog.style.height = `${viewportHeight * this.SNAP_RATIO}px`
        this.dialog.style.maxWidth = `${viewportWidth}px`
        this.dialog.style.maxHeight = `${viewportHeight * this.SNAP_RATIO}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.bottom = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to top-left corner
     */
    private snapToTopLeft(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = '0px'
        this.dialog.style.top = '0px'
        this.dialog.style.width = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.height = `${viewportHeight * this.SNAP_RATIO}px`
        this.dialog.style.maxWidth = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.maxHeight = `${viewportHeight * this.SNAP_RATIO}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.topLeft = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to top-right corner
     */
    private snapToTopRight(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const rightOffset = viewportWidth * (1 - this.SNAP_RATIO)
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = `${rightOffset}px`
        this.dialog.style.top = '0px'
        this.dialog.style.width = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.height = `${viewportHeight * this.SNAP_RATIO}px`
        this.dialog.style.maxWidth = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.maxHeight = `${viewportHeight * this.SNAP_RATIO}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.topRight = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to bottom-left corner
     */
    private snapToBottomLeft(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const bottomOffset = viewportHeight * (1 - this.SNAP_RATIO)
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = '0px'
        this.dialog.style.top = `${bottomOffset}px`
        this.dialog.style.width = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.height = `${viewportHeight * this.SNAP_RATIO}px`
        this.dialog.style.maxWidth = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.maxHeight = `${viewportHeight * this.SNAP_RATIO}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.bottomLeft = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to bottom-right corner
     */
    private snapToBottomRight(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const rightOffset = viewportWidth * (1 - this.SNAP_RATIO)
        const bottomOffset = viewportHeight * (1 - this.SNAP_RATIO)
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = `${rightOffset}px`
        this.dialog.style.top = `${bottomOffset}px`
        this.dialog.style.width = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.height = `${viewportHeight * this.SNAP_RATIO}px`
        this.dialog.style.maxWidth = `${viewportWidth * this.SNAP_RATIO}px`
        this.dialog.style.maxHeight = `${viewportHeight * this.SNAP_RATIO}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.bottomRight = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Snap to maximize (full screen)
     */
    private snapToMaximize(): void {
        // Clear all snap intention timers
        this.clearSnapIntentionTimers()
        
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        
        // Add snapping class for transition effect
        this.dialog.classList.add('snapping')
        
        this.dialog.style.transition = `all ${this.SNAP_ANIMATION_DURATION}ms ease`
        this.dialog.style.left = `${this.IFRAME_BUFFER}px`
        this.dialog.style.top = `${this.IFRAME_BUFFER}px`
        this.dialog.style.width = `${viewportWidth - this.IFRAME_BUFFER * 2}px`
        this.dialog.style.height = `${viewportHeight - this.IFRAME_BUFFER * 2}px`
        this.dialog.style.maxWidth = `${viewportWidth - this.IFRAME_BUFFER * 2}px`
        this.dialog.style.maxHeight = `${viewportHeight - this.IFRAME_BUFFER * 2}px`
        
        this.isSnapped = true
        this.resetSnapState()
        this.snapState.maximized = true
        
        // Reset drag state
        this.isDragging = false
        
        setTimeout(() => {
            this.dialog.style.transition = 'none'
            this.dialog.classList.remove('snapping')
        }, this.SNAP_ANIMATION_DURATION)
    }

    /**
     * Create a snap dialog with an iframe and attach it to the document body
     * @param targetElement The element that triggered the creation of the dialog
     * @param iframeUrl The URL to load in the iframe
     * @param options Optional configuration options
     * @returns A function to destroy the dialog
     */
    public static createWithIframe(targetElement: HTMLElement, iframeUrl: string, options: Partial<SnapDialogConfig> = {}): () => void {
        if (!targetElement || !iframeUrl) {
            console.error("Element or URL not provided")
            return () => {}
        }

        // Check if an iframe dialog already exists and remove it
        const existingDialog = document.querySelector('.iframe-dialog')
        if (existingDialog) {
            existingDialog.remove()
        }
        
        // Create dialog container
        const iframeDialog = document.createElement("div")
        iframeDialog.className = "iframe-dialog"
        
        // Create dialog header with title and close button
        const dialogHeader = document.createElement("div")
        dialogHeader.className = "iframe-dialog-header"
        
        // Title
        const dialogTitle = document.createElement("div")
        dialogTitle.className = "iframe-dialog-title"
        dialogTitle.textContent = options.title || "Documentation"
        dialogHeader.appendChild(dialogTitle)
        
        // Controls container for buttons
        const controlsContainer = document.createElement("div")
        controlsContainer.className = "iframe-dialog-controls"
        
        // Collapse button
        const collapseButton = document.createElement("div")
        collapseButton.className = "iframe-dialog-collapse"
        collapseButton.innerHTML = '<i class="fas fa-minus"></i>'
        controlsContainer.appendChild(collapseButton)
        
        // Close button
        const closeButton = document.createElement("div")
        closeButton.className = "iframe-dialog-close"
        closeButton.innerHTML = '<i class="fas fa-times"></i>'
        controlsContainer.appendChild(closeButton)
        
        // Add controls to header
        dialogHeader.appendChild(controlsContainer)
        
        iframeDialog.appendChild(dialogHeader)
        
        // Create iframe container
        const iframeContainer = document.createElement("div")
        iframeContainer.className = "iframe-dialog-content"
        
        // Create iframe
        const iframe = document.createElement("iframe")
        iframe.src = iframeUrl
        iframe.width = "100%"
        iframe.height = "100%"
        iframe.frameBorder = "0"
        iframe.allowFullscreen = true
        iframeContainer.appendChild(iframe)
        
        iframeDialog.appendChild(iframeContainer)
        document.body.appendChild(iframeDialog)
        
        // Position dialog horizontally based on the click event's X position
        if (targetElement.getBoundingClientRect) {
            const clickRect = targetElement.getBoundingClientRect()
            const clickX = clickRect.left + (clickRect.width / 2)
            const dialogWidth = iframeDialog.offsetWidth
            let leftPosition = clickX - (dialogWidth / 2)
            
            // Keep the dialog within the viewport
            const viewportWidth = window.innerWidth
            if (leftPosition < 20) {
                leftPosition = 20 // 20px margin from left edge
            } else if (leftPosition + dialogWidth > viewportWidth - 20) {
                leftPosition = viewportWidth - dialogWidth - 20 // 20px margin from right edge
            }
            
            iframeDialog.style.left = leftPosition + "px"
            // Y position is fixed in CSS at 100px from top
        }
        
        // Add entrance animation
        iframeDialog.classList.add("animate__animated", "animate__fadeIn", "animate__faster")
        
        // Initialize the SnapDialog with Windows 11-style snapping
        const snapDialogInstance = new SnapDialog(iframeDialog, dialogHeader, {
            ...options,
            onClose: () => {
                if (options.onClose) options.onClose()
                iframeDialog.classList.add("animate__fadeOut")
                setTimeout(() => {
                    iframeDialog.remove()
                }, 300)
            }
        })
        
        // Add close button click handler
        closeButton.addEventListener("click", () => {
            if (snapDialogInstance.onClose) {
                snapDialogInstance.onClose()
            }
        })
        
        // Store the snapDialog instance on the iframe element to prevent memory leaks
        iframeDialog.snapDialog = snapDialogInstance
        
        return () => {
            if (iframeDialog) {
                if (iframeDialog.snapDialog) {
                    iframeDialog.snapDialog.destroy()
                }
                iframeDialog.remove()
            }
        }
    }
}